/**
 * RedactionPlanForm — Carte de validation du lancement du plan initial.
 *
 * WIRING (2026-08-17) : l'acte est transmis par son `actId` canonique. Le
 * backend revalide l'identité exacte et la disponibilité du prompt au clic ;
 * le formulaire ne reconstruit jamais nom/spécialisation/procédure depuis du
 * texte libre.
 *
 * Boutons : Lancer (envoie `redaction_plan_response { accepted: true }`)
 *           Annuler (envoie `redaction_plan_cancelled`)
 */

import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { ArrowRight, X, FileText, Scale, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  defaultSearchJurisdictionsForSpecialisation,
  jurisdictionsForSpecialisation,
} from '@hector/shared-types';
import type {
  AvailableActCatalogEntry,
  RedactionPlanDraftUpdated,
  RedactionPlanDraftSnapshot,
} from '@hector/shared-types';
import { ALL_JURISDICTIONS, JURISDICTION_LABELS } from '../jurisprudence/types';
import type { Jurisdiction } from '../jurisprudence/types';
import { ActSourceSelector } from './ActSourceSelector';
import {
  evaluateWebActSourceReadiness,
  initialSelectionFromProposal,
  selectCanonicalAct,
} from './redactionSourceSelection';
import { useAdversePleadingsCandidates } from './useAdversePleadingsCandidates';
import { useOwnPriorPleadingsCandidates } from './useOwnPriorPleadingsCandidates';
import { useAttackedDecisionCandidates } from './useAttackedDecisionCandidates';

/**
 * Juridictions DISPONIBLES pour une spécialité. La liste complète reste visible
 * pour permettre un choix explicite de TJ/TCOM ; la sélection initiale est portée
 * par `defaultSearchJurisdictionsForSpecialisation` juste en dessous.
 */
function relevantJurisdictionsFor(specialisation?: string | null): Jurisdiction[] {
  const fromSpec = jurisdictionsForSpecialisation(specialisation);
  return fromSpec && fromSpec.length > 0 ? Array.from(fromSpec) : [...ALL_JURISDICTIONS];
}

/**
 * Sélection initiale : une liste explicite du LLM est reflétée dans la carte ;
 * en son absence, CC/CA sont utilisés pour l'ordre judiciaire et CE/CAA/TA pour
 * l'ordre administratif. Ainsi l'interface ne contredit jamais une intention
 * explicite, sans réintroduire les juridictions de première instance par défaut.
 */
function initialJurisdictions(
  specialisation?: string | null,
  proposedJurisdictions?: string[],
): Jurisdiction[] {
  const relevant = relevantJurisdictionsFor(specialisation);
  const explicit = (proposedJurisdictions ?? [])
    .map((jurisdiction) => jurisdiction.trim().toUpperCase())
    .filter((jurisdiction): jurisdiction is Jurisdiction => relevant.includes(jurisdiction as Jurisdiction));
  if (explicit.length > 0) return [...new Set(explicit)];
  return [...defaultSearchJurisdictionsForSpecialisation(specialisation)];
}

interface ProposedPayload {
  actId?: string;
  nom_acte?: string | null;
  specialisation?: string | null;
  /** Wire legacy conservé ; les défauts viennent désormais de la politique partagée. */
  juridictions?: string[];
  additional_instructions?: string;
  original_job_id?: string;
  generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
  /** Intention LLM sur la recherche JP — pré-remplit le toggle. */
  skip_jurisprudence?: boolean;
  /**
   * Lancement en un seul temps : ce job produira l'acte, pas un plan à valider.
   * `kind` vaut pourtant `'research'` — voir `productionRedigee`.
   */
  full_document?: boolean;
  /** Actes lançables avec prompt exact; absent = carte historique en lecture seule. */
  availableActCatalog?: AvailableActCatalogEntry[];
  /**
   * Présélection proposée par Hector, déjà confrontée au dossier côté serveur.
   * La carte ne coche que ce qu'elle retrouve dans ses propres candidats.
   */
  sources?: import('@hector/shared-types').RedactionProposedPayload['sources'];
}

/**
 * Ordre d'affichage des sources : ce à quoi l'acte RÉPOND, ce qu'il PROLONGE,
 * puis ce qu'il ATTAQUE. C'est l'ordre dans lequel l'avocat pense sa réplique,
 * et il ne dépend pas de l'ordre de déclaration au catalogue.
 */
const ORDRE_DES_SOURCES = [
  'adverse_pleadings',
  'own_prior_pleading',
  'attacked_decision',
] as const satisfies readonly import('@hector/shared-types').ActSourceKind[];

interface RedactionPlanFormProps {
  requestId: string;
  caseId?: string;
  /** Échéance absolue du backend. Absente uniquement avec un backend plus ancien. */
  autoLaunchAt?: string;
  /** Dernière revision envoyée, conservée par la timeline pour replay. */
  autoLaunchDraftUpdatePending?: RedactionPlanDraftUpdated['payload'];
  /** Dernier brouillon accepté par le backend, conservé après l'ACK. */
  autoLaunchDraftSnapshot?: RedactionPlanDraftSnapshot;
  proposedPayload: ProposedPayload;
  /**
   * `skipJurisprudence` + `overrides` ne sont envoyés que si l'avocat a touché le
   * champ (kind=research) — sinon undefined → le backend garde la proposition agent.
   * Le catalogue d'actes vit dans `proposedPayload.availableActCatalog`.
   */
  onSubmit: (
    requestId: string,
    additionalInstructions: string | undefined,
    skipJurisprudence?: boolean,
    overrides?: {
      actId?: string;
      juridictions?: string[];
      sources?: NonNullable<import('@hector/shared-types').RedactionPlanResponsePayload['sources']>;
    },
  ) => void;
  onCancel: (requestId: string) => void;
  onInteract: (requestId: string, snapshot: RedactionPlanDraftSnapshot) => boolean;
  isLoading?: boolean;
}

/** Textarea qui se redimensionne automatiquement à la hauteur de son contenu. */
function AutoResizeTextarea({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(48, el.scrollHeight)}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={2}
      className="w-full resize-none overflow-hidden text-sm text-stone-800 placeholder:text-stone-400 bg-white border border-stone-200 rounded-lg p-3 focus:outline-none focus:border-stone-400 leading-relaxed"
    />
  );
}

const FIELD_LABEL_CLASS = 'text-[10px] uppercase tracking-wide text-stone-400 font-medium';

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Libellé de champ + mention « suggéré » (italique) quand la valeur = la proposition de l'agent. */
function FieldLabel({ label, suggested }: { label: string; suggested: boolean }) {
  return (
    <p className={FIELD_LABEL_CLASS + ' mb-0.5 flex items-center gap-1.5'}>
      {label}
      {suggested && (
        <span className="italic normal-case tracking-normal font-normal text-stone-400 text-[10px]">suggéré</span>
      )}
    </p>
  );
}

/** Sélecteur d'acte searchable (Popover + Command). Fallback lecture seule si pas de catalogue. */
function ActeSelector({
  selected,
  options,
  fallbackLabel,
  onChange,
  disabled,
}: {
  selected?: AvailableActCatalogEntry;
  options?: AvailableActCatalogEntry[];
  fallbackLabel?: string;
  onChange: (act: AvailableActCatalogEntry) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasCatalog = !!options && options.length > 0;

  if (!hasCatalog) {
    return (
      <p className="text-sm text-stone-800 truncate flex items-center gap-1">
        <FileText className="h-3 w-3 text-stone-500 shrink-0" />
        {selected?.displayLabel || fallbackLabel || '…'}
      </p>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="mt-0.5 w-full flex items-center gap-1.5 text-left text-sm text-stone-800 disabled:opacity-60"
        >
          <FileText className="h-3 w-3 text-stone-500 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{selected?.displayLabel || 'Choisir un acte…'}</span>
          <ChevronDown className="h-3.5 w-3.5 text-stone-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-0 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border-stone-200 p-0 shadow-[0_10px_24px_-16px_rgba(28,25,23,0.45)]"
        align="start"
        sideOffset={2}
      >
        <Command>
          <CommandInput placeholder="Rechercher un acte…" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>Aucun acte trouvé</CommandEmpty>
            <CommandGroup>
              {options.map((acte) => (
                <CommandItem
                  key={acte.id}
                  value={`${acte.procedure} ${acte.subProcedure ?? ''} ${acte.displayLabel}`}
                  disabled={!acte.available}
                  onSelect={() => {
                    onChange(acte);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', acte.id === selected?.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0">
                    <span className="block truncate">{acte.displayLabel}</span>
                    <span className="block whitespace-normal text-[10px] leading-tight text-stone-500">
                      {acte.procedure}{acte.subProcedure ? ` · ${acte.subProcedure}` : ''}
                      {!acte.available
                        ? ` · ${acte.unavailableReason ?? 'Indisponible : prompt exact non configuré.'}`
                        : ''}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function RedactionPlanForm({
  requestId,
  caseId,
  autoLaunchAt,
  autoLaunchDraftUpdatePending,
  autoLaunchDraftSnapshot,
  proposedPayload,
  onSubmit,
  onCancel,
  onInteract,
  isLoading = false,
}: RedactionPlanFormProps) {
  // Pending wins while an edit is in flight. Once the backend acknowledges it,
  // the durable snapshot keeps the remounted card aligned with the payload that
  // the backend will launch when the deadline expires.
  const initialDraftSnapshot = autoLaunchDraftUpdatePending?.snapshot ?? autoLaunchDraftSnapshot;
  const [instructions, setInstructions] = useState<string>(
    initialDraftSnapshot?.additional_instructions ?? proposedPayload.additional_instructions ?? '',
  );
  // Acte canonique + juridictions sélectionnables pour le plan détaillé. Le backend ne fait jamais
  // confiance aux libellés : seul actId traverse le contrat de lancement.
  // Le backend filtre déjà via `listForAgent`. Ce second filtre protège la carte
  // contre un payload issu d'un backend plus ancien qui annoterait encore les
  // positions canoniques sans prompt au lieu de les omettre.
  const actCatalog = proposedPayload.availableActCatalog?.filter((act) => act.available);
  // A pending snapshot is authoritative even when it explicitly cleared the
  // act after a specialty change; null must not fall back to the proposal.
  const initialActId = initialDraftSnapshot ? initialDraftSnapshot.actId : proposedPayload.actId;
  const initialCanonicalAct = actCatalog?.find((act) => act.id === initialActId);
  const [actId, setActId] = useState<string>(initialActId ?? '');
  // La présélection d'Hector est l'état INITIAL, pas un affichage à part : dès
  // le premier rendu, ce que la carte montre coché est ce qu'elle enverra. Un
  // affichage « suggéré » distinct de la sélection réelle ferait diverger ce que
  // l'avocat voit de ce qu'il valide.
  const pendingSources = initialDraftSnapshot?.sources?.map((source) => ({
    kind: source.kind,
    documentIds: source.references.flatMap((reference) =>
      reference.referenceType === 'case_document' ? [reference.documentId] : []),
  }));
  const suggestedByKind = initialSelectionFromProposal(
    initialCanonicalAct,
    pendingSources ?? proposedPayload.sources,
  );
  const [selectedDocumentIdsByKind, setSelectedDocumentIdsByKind] = useState<Partial<Record<import('@hector/shared-types').ActSourceKind, string[]>>>(
    () => suggestedByKind,
  );
  const [nomActe, setNomActe] = useState<string>(
    initialDraftSnapshot?.nom_acte ?? initialCanonicalAct?.backendName ?? proposedPayload.nom_acte ?? '',
  );
  const [specialisation, setSpecialisation] = useState<string>(
    initialDraftSnapshot?.specialisation ?? initialCanonicalAct?.specializationLabel ?? proposedPayload.specialisation ?? '',
  );
  const [juridictions, setJuridictions] = useState<Jurisdiction[]>(() => initialJurisdictions(
    initialDraftSnapshot?.specialisation ?? initialCanonicalAct?.specializationLabel ?? proposedPayload.specialisation,
    initialDraftSnapshot?.jurisdictions ?? proposedPayload.juridictions,
  ));
  // Juridictions DISPONIBLES = celles pertinentes pour la spécialité SÉLECTIONNÉE.
  const relevantJurisdictions = relevantJurisdictionsFor(specialisation);
  const [juridictionsTouched, setJuridictionsTouched] = useState<boolean>(false);

  // Word et le tool `list_available_acts` ne proposent que les actes avec prompt
  // exact ; aucune position indisponible ne doit donc apparaître dans la carte.
  const specialtyOptions: string[] = actCatalog
    ? [...new Set(actCatalog.map((act) => act.specializationLabel))]
    : specialisation ? [specialisation] : [];
  const actOptions = actCatalog?.filter((act) => act.specializationLabel === specialisation);
  const selectedAct = actCatalog?.find((act) => act.id === actId);
  const sourceReadiness = evaluateWebActSourceReadiness(selectedAct, selectedDocumentIdsByKind);
  const rulesByKind = new Map((selectedAct?.sources ?? []).map((rule) => [rule.kind, rule]));
  const adverseCandidates = useAdversePleadingsCandidates(
    caseId,
    rulesByKind.has('adverse_pleadings'),
  );
  const ownPriorCandidates = useOwnPriorPleadingsCandidates(
    caseId,
    rulesByKind.has('own_prior_pleading'),
  );
  const attackedDecisionCandidates = useAttackedDecisionCandidates(
    caseId,
    rulesByKind.has('attacked_decision'),
  );
  const candidatesByKind = {
    adverse_pleadings: adverseCandidates,
    own_prior_pleading: ownPriorCandidates,
    attacked_decision: attackedDecisionCandidates,
  } as const;
  const reglesAffichees = ORDRE_DES_SOURCES.flatMap((kindOfSource) => {
    const rule = rulesByKind.get(kindOfSource);
    return rule ? [rule] : [];
  });
  const [launchState, setLaunchState] = useState<'armed' | 'disarmed'>(
    autoLaunchAt ? 'armed' : 'disarmed',
  );
  const [remainingSeconds, setRemainingSeconds] = useState(() => autoLaunchAt
    ? Math.max(0, Math.ceil((Date.parse(autoLaunchAt) - Date.now()) / 1000))
    : 0);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  // Keep the countdown visible during the short round-trip that confirms a
  // draft edit. The backend remains authoritative: this provisional deadline
  // is replaced as soon as its versioned ACK updates `autoLaunchAt`.
  const [optimisticAutoLaunchAt, setOptimisticAutoLaunchAt] = useState<string>();
  const previousAutoLaunchAt = useRef(autoLaunchAt);
  const displayedAutoLaunchAt = optimisticAutoLaunchAt ?? autoLaunchAt;

  useEffect(() => {
    if (!displayedAutoLaunchAt || launchState !== 'armed') return;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((Date.parse(displayedAutoLaunchAt) - Date.now()) / 1000));
      setRemainingSeconds(remaining);
    };
    update();
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [displayedAutoLaunchAt, launchState]);

  useEffect(() => {
    // The browser only projects a backend-owned deadline. A draft ACK replaces
    // that projection, but a local 0:00 never changes the card state: only
    // `redaction_plan_launch_started` may replace it with launch progress.
    const deadlineChanged = previousAutoLaunchAt.current !== autoLaunchAt;
    previousAutoLaunchAt.current = autoLaunchAt;
    if (deadlineChanged) {
      // The ACK is the source of truth once it arrives; until then the local
      // provisional deadline keeps the control out of the expired state.
      setOptimisticAutoLaunchAt(undefined);
      if (!manualSubmitting) setLaunchState(autoLaunchAt ? 'armed' : 'disarmed');
    }
  }, [autoLaunchAt, manualSubmitting]);

  const buildDraftSnapshot = ({
    nextActId = actId,
    nextNomActe = nomActe,
    nextSpecialisation = specialisation,
    nextJuridictions = juridictions,
    nextSkipJp = skipJp,
    nextInstructions = instructions,
    nextSelectedDocumentIdsByKind = selectedDocumentIdsByKind,
  }: {
    nextActId?: string;
    nextNomActe?: string;
    nextSpecialisation?: string;
    nextJuridictions?: Jurisdiction[];
    nextSkipJp?: boolean;
    nextInstructions?: string;
    nextSelectedDocumentIdsByKind?: Partial<Record<import('@hector/shared-types').ActSourceKind, string[]>>;
  } = {}): RedactionPlanDraftSnapshot => {
    const nextSelectedAct = actCatalog?.find((act) => act.id === nextActId);
    const nextReadiness = evaluateWebActSourceReadiness(nextSelectedAct, nextSelectedDocumentIdsByKind);
    return {
      actId: nextActId || null,
      nom_acte: nextNomActe ?? '',
      specialisation: nextSpecialisation ?? '',
      jurisdictions: [...(nextJuridictions ?? [])],
      additional_instructions: nextInstructions ?? '',
      skip_jurisprudence: nextSkipJp ?? false,
      sources: nextReadiness.sources,
    };
  };

  const notifyDraftUpdate = (snapshot: RedactionPlanDraftSnapshot): boolean => {
    // A legacy backend has no replacement deadline after its ACK, but the
    // browser must still persist every later edit in the timeline snapshot.
    // Otherwise a remount would silently resurrect the first acknowledged edit.
    if (!autoLaunchAt && !initialDraftSnapshot) return true;
    const accepted = onInteract(requestId, snapshot);
    if (accepted && autoLaunchAt) {
      // Reset locally at the same user-visible instant as the edit. The
      // authoritative backend ACK below will replace this with its exact ISO
      // deadline, so network latency cannot make the timer disappear.
      setOptimisticAutoLaunchAt(new Date(Date.now() + 60_000).toISOString());
      setRemainingSeconds(60);
    }
    return accepted;
  };

  // The countdown stays armed while a draft update is in flight. The backend
  // ACK replaces `autoLaunchAt`; a remount therefore renders the same deadline
  // instead of falling into the old permanent-disarm state.
  const controlsDisabled = isLoading || manualSubmitting;
  const launchDisabled = controlsDisabled || (
    !actId
    || selectedAct?.available !== true
    || sourceReadiness?.canLaunch !== true
  );

  const handleActChange = (act: AvailableActCatalogEntry) => {
    if (act.id === actId) return;
    const next = selectCanonicalAct({ actId, selectedDocumentIdsByKind }, act.id);
    if (!notifyDraftUpdate(buildDraftSnapshot({
      nextActId: next.actId,
      nextNomActe: act.backendName,
      nextSelectedDocumentIdsByKind: next.selectedDocumentIdsByKind,
    }))) return;
    setActId(next.actId);
    setSelectedDocumentIdsByKind(next.selectedDocumentIdsByKind);
    setNomActe(act.backendName);
  };

  // Changer de spécialité ré-adapte les juridictions ET la liste d'actes : l'acte est
  // remis à zéro s'il n'appartient pas à la nouvelle spécialité (modèle add-in).
  const handleSpecialisationChange = (value: string) => {
    if (value === specialisation) return;
    const nextAct = selectedAct?.specializationLabel === value ? selectedAct : undefined;
    const nextJuridictions = [...defaultSearchJurisdictionsForSpecialisation(value)];
    if (!notifyDraftUpdate(buildDraftSnapshot({
      nextActId: nextAct?.id ?? '',
      nextNomActe: nextAct?.backendName ?? '',
      nextSpecialisation: value,
      nextJuridictions,
      nextSelectedDocumentIdsByKind: nextAct ? selectedDocumentIdsByKind : {},
    }))) return;
    setSpecialisation(value);
    // Le changement de spécialité réinitialise la sélection avec la politique
    // métier ; la liste complète reste disponible pour un choix explicite.
    setJuridictions(nextJuridictions);
    setJuridictionsTouched(true);
    if (selectedAct?.specializationLabel !== value) {
      setActId('');
      setSelectedDocumentIdsByKind({});
      setNomActe('');
    }
  };
  // skip JP : pré-rempli avec la valeur proposée par le LLM. Track le « touché »
  // pour n'envoyer que les overrides réels — le backend interprète undefined
  // comme « garde la valeur LLM ».
  const initialSkipJp = initialDraftSnapshot?.skip_jurisprudence ?? (proposedPayload.skip_jurisprudence === true);
  const [skipJp, setSkipJp] = useState<boolean>(initialSkipJp);
  const [skipJpTouched, setSkipJpTouched] = useState<boolean>(false);

  const toggleJurisdiction = (jur: Jurisdiction) => {
    const has = juridictions.includes(jur);
    if (has && juridictions.length === 1) return; // garder au moins une juridiction
    const nextJuridictions = has ? juridictions.filter((j) => j !== jur) : [...juridictions, jur];
    if (!notifyDraftUpdate(buildDraftSnapshot({ nextJuridictions }))) return;
    setJuridictionsTouched(true);
    setJuridictions(nextJuridictions);
  };

  const handleSubmit = () => {
    if (launchDisabled) return;
    // L'acte canonique est toujours renvoyé afin que le collector puisse
    // revalider le snapshot exact ayant servi à afficher la carte.
    setManualSubmitting(true);
    // After a transport ACK/remount, this snapshot is the complete state the
    // lawyer sees. Send every override — including false/empty values — so a
    // legacy backend cannot fall back to the original proposal on manual click.
    const hasPersistedDraft = initialDraftSnapshot !== undefined;
    const overrides = {
      ...(actId ? { actId } : {}),
      ...(juridictionsTouched || hasPersistedDraft ? { juridictions } : {}),
      ...(sourceReadiness ? { sources: sourceReadiness.sources } : {}),
    };
    onSubmit(
      requestId,
      hasPersistedDraft ? instructions.trim() : (instructions.trim() || undefined),
      skipJpTouched || hasPersistedDraft ? skipJp : undefined,
      overrides && Object.keys(overrides).length > 0 ? overrides : undefined,
    );
  };
  const handleCancel = () => {
    onCancel(requestId);
  };

  // Ce que ce lancement produira, pas quel job tourne : en un seul temps le
  // `kind` reste 'research' alors que c'est l'acte qui sortira. Promettre « le
  // plan » ici ferait accepter à l'avocat autre chose que ce qu'il obtient.
  const production = proposedPayload.full_document === true ? 'acte' : 'plan';
  const headerLabel =
    production === 'plan'
      ? 'Lancer la recherche pour le plan de l\'acte'
      : 'Lancer la rédaction de l\'acte complet';
  const subLabel =
    production === 'plan'
      ? 'Le plan structuré (arguments + jurisprudence + faits) sera rédigé dans le canvas latéral.'
      : 'La recherche et la rédaction s\'enchaînent : l\'acte sera écrit en direct dans le canvas, sans plan à valider.';

  return (
    <div className="my-3 rounded-xl border border-stone-200 bg-white shadow-soft overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50">
        <div className="rounded-md bg-stone-900 text-white p-1.5">
          <Scale className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-900 leading-tight">{headerLabel}</p>
          <p className="text-xs text-stone-500 mt-0.5">{subLabel}</p>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        <>
            {/* Spécialité (SÉLECTIONNABLE) — au-dessus. Pilote les juridictions proposées. */}
            <div className="rounded-md border border-stone-200 bg-white px-3 py-2 text-xs">
              <FieldLabel label="Spécialité" suggested={!!specialisation && specialisation === proposedPayload.specialisation} />
              <Select value={specialisation || undefined} onValueChange={handleSpecialisationChange} disabled={controlsDisabled}>
                <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-sm text-stone-800 shadow-none focus:ring-0">
                  <SelectValue placeholder="Choisir une spécialité…" />
                </SelectTrigger>
                <SelectContent>
                  {(specialisation && !specialtyOptions.includes(specialisation)
                    ? [specialisation, ...specialtyOptions]
                    : specialtyOptions
                  ).map((spec) => (
                    <SelectItem key={spec} value={spec}>
                      {spec}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Acte (sélectionnable) — ligne entière (le nom d'acte est long, il doit être lisible). */}
            <div className="rounded-md border border-stone-200 bg-white px-3 py-2 text-xs">
              <FieldLabel label="Acte" suggested={!!nomActe && nomActe === proposedPayload.nom_acte} />
              <ActeSelector
                selected={selectedAct}
                options={actOptions}
                fallbackLabel={actCatalog ? undefined : nomActe}
                onChange={handleActChange}
                disabled={controlsDisabled}
              />
            </div>
        </>

        {/* Les pièces du dossier qui fondent l'acte. Une seule section, un bloc
            par règle du catalogue — plus de sélecteurs détachés ni de bandeaux
            d'import posés en vrac sous la liste. */}
        {reglesAffichees.length > 0 && (
          <div className="space-y-2">
            <p className={FIELD_LABEL_CLASS}>Pièces du dossier</p>
            {reglesAffichees.map((rule) => {
              const query = candidatesByKind[rule.kind];
              return (
                <ActSourceSelector
                  key={rule.kind}
                  rule={rule}
                  publicLaw={selectedAct?.specialization === 'public'}
                  candidates={query.data}
                  selectedIds={selectedDocumentIdsByKind[rule.kind] ?? []}
                  suggestedIds={suggestedByKind[rule.kind] ?? []}
                  onChange={(ids) => {
                    const currentIds = selectedDocumentIdsByKind[rule.kind] ?? [];
                    const sameSelection = currentIds.length === ids.length
                      && currentIds.every((id) => ids.includes(id));
                    if (sameSelection) return;
                    const nextSelectedDocumentIdsByKind = {
                      ...selectedDocumentIdsByKind,
                      [rule.kind]: ids,
                    };
                    if (!notifyDraftUpdate(buildDraftSnapshot({ nextSelectedDocumentIdsByKind }))) return;
                    setSelectedDocumentIdsByKind((current) => ({ ...current, [rule.kind]: ids }));
                  }}
                  isLoading={query.isLoading}
                  error={query.error instanceof Error ? query.error : null}
                  disabled={controlsDisabled}
                  unmetReason={sourceReadiness?.reasonByKind[rule.kind] ?? null}
                />
              );
            })}
          </div>
        )}

        {/* Blocage qui ne vise aucune règle en particulier : acte indisponible,
            catalogue absent, ou type de source pas encore sélectionnable. */}
        {selectedAct && sourceReadiness?.reason && (
          <p role="status" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {sourceReadiness.reason}
          </p>
        )}

        {/* Recherche JP (research-only) — switch clair on/off « Rechercher la
            jurisprudence » (variante B, choix produit 2026-07-21). Off → plan sans JP. */}
        {(
          <div className="rounded-md border border-stone-100 bg-stone-50 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Scale className="h-3.5 w-3.5 text-stone-500" />
                <span className="text-sm text-stone-800">Rechercher la jurisprudence</span>
              </div>
              <Switch
                checked={!skipJp}
                disabled={controlsDisabled}
                onCheckedChange={(checked) => {
                  const nextSkipJp = !checked;
                  if (nextSkipJp === skipJp) return;
                  if (!notifyDraftUpdate(buildDraftSnapshot({ nextSkipJp }))) return;
                  setSkipJp(!checked);
                  setSkipJpTouched(true);
                }}
                aria-label="Activer ou désactiver la recherche de jurisprudence"
              />
            </div>
            {!skipJp ? (
              <>
                <p className={FIELD_LABEL_CLASS + ' mt-2.5'}>Juridictions</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {relevantJurisdictions.map((jur) => {
                    const selected = juridictions.includes(jur);
                    return (
                      <button
                        key={jur}
                        type="button"
                        disabled={controlsDisabled}
                        onClick={() => toggleJurisdiction(jur)}
                        aria-pressed={selected}
                        title={JURISDICTION_LABELS[jur]}
                        className={cn(
                          'px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all duration-150 select-none',
                          selected
                            ? 'bg-stone-900 text-white border-stone-900'
                            : 'bg-white text-stone-400 border-stone-200 hover:border-stone-400 hover:text-stone-600',
                        )}
                      >
                        {jur}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-[11px] text-stone-500 mt-1.5">
                Plan construit sans jurisprudence. Plus rapide (~30 s), tu apporteras les références toi-même.
              </p>
            )}
          </div>
        )}

        {/* Champ éditable : additional_instructions */}
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1.5">
            Instructions complémentaires {' '}
            <span className="text-[10px] uppercase tracking-wide text-stone-400 font-medium">· éditable</span>
          </label>
          <AutoResizeTextarea
            value={instructions}
            onChange={(value) => {
              if (value === instructions) return;
              if (!notifyDraftUpdate(buildDraftSnapshot({ nextInstructions: value }))) return;
              setInstructions(value);
            }}
            placeholder="Précisez l'angle, les pièces à prioriser, le ton voulu… (optionnel)"
            disabled={controlsDisabled}
          />
          <p className="text-[11px] text-stone-400 mt-1">
            Hector a proposé ces instructions. Modifie-les si besoin avant de lancer.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-stone-100 bg-stone-50">
        <button
          type="button"
          onClick={handleCancel}
          disabled={controlsDisabled}
          className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 transition-colors px-2.5 py-1.5 rounded-md disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Annuler
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={launchDisabled}
          aria-label="Lancer"
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors',
            launchDisabled
              ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
              : 'bg-stone-900 text-white hover:bg-stone-800',
          )}
        >
          {manualSubmitting
            ? 'Lancement en cours…'
            : launchState === 'armed'
              ? `Lancer · ${formatCountdown(remainingSeconds)}`
              : 'Lancer'}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
        <span className="sr-only" aria-live="polite">
          {launchState === 'disarmed'
            ? 'Lancement automatique indisponible.'
            : ''}
        </span>
      </div>
    </div>
  );
}
